import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { updateThemeData } from "../../admin/functions/updateThemeData";
import { uploadYogaMediaViaPresign } from "../../utils/yogaMediaUpload";
import { addReportToServer } from "../../functions/sendReport";
import { apiUrl } from "../../utils/apiUrl";
import "./uploadImage.css";

const ACCEPTED_MIME = new Set(["image/jpeg", "image/jpg", "image/png"]);
const MAX_BYTES = 2 * 1024 * 1024; // 2MB
const WORDS_MAX_LEN = 15;

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return b || (typeof window !== "undefined" ? window.location.origin : "");
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

function isDemoUser(user) {
  return (
    user?.source === "DEMO" ||
    user?.role === "DEMO" ||
    user?.role === "demobypass" ||
    String(user?.sessionId || "")
      .toLowerCase()
      .startsWith("demo")
  );
}

function isWordMode(themeData) {
  return String(themeData?.submission_mode || "").trim().toLowerCase() === "word";
}

function parsePlayerWordsList(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function wordDoneStorageKey(user) {
  if (!user?.userId || !user?.sessionId) return null;
  return `mosaic_word_done_${user.sessionId}_${user.userId}`;
}

function markWordDone(user) {
  const key = wordDoneStorageKey(user);
  if (!key) return;
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

function hasWordDone(user) {
  const key = wordDoneStorageKey(user);
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

async function redirectDemoToPlans() {
  await Swal.fire({
    icon: "success",
    title: "Thank you for playing.",
    text: "Subscribe to any PLAN to play with your peers.",
  });
  window.location.href = demoPlansUrl();
}

export default function UploadImage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: themeData, currentTheme } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const wordMode = isWordMode(themeData);

  const [selfieMode, setSelfieMode] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [playerWord, setPlayerWord] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const textColor = themeData?.text_color || "#000000";

  const btnStyle = {
    ...(themeData?.button_backgroundcolor && {
      backgroundColor: themeData.button_backgroundcolor,
    }),
    ...(themeData?.button_color && { color: themeData.button_color }),
  };

  const clearPreview = useCallback(() => {
    setPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return "";
    });
    setImageFile(null);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
    setCameraOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      clearPreview();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      setSelfieMode(true);
      setCameraOn(true);
    } catch (e) {
      Swal.fire(
        "Camera unavailable",
        "Could not access the camera. Please allow camera permission or upload a file instead.",
        "error"
      );
    }
  }, [clearPreview]);

  const takeSnapshot = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        if (file.size > MAX_BYTES) {
          Swal.fire("Too large", "Captured image exceeds 2MB.", "warning");
          return;
        }
        setImageFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        stopCamera();
      },
      "image/jpeg",
      0.9
    );
  }, [stopCamera]);

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (!ACCEPTED_MIME.has(String(file.type).toLowerCase())) {
        Swal.fire("Invalid file", "Please choose a JPG, JPEG or PNG image.", "warning");
        return;
      }
      if (file.size > MAX_BYTES) {
        Swal.fire("File too large", "The file must be less than 2MB.", "warning");
        return;
      }
      stopCamera();
      setSelfieMode(false);
      setImageFile(file);
      setPreviewUrl((url) => {
        if (url) URL.revokeObjectURL(url);
        return URL.createObjectURL(file);
      });
    },
    [stopCamera]
  );

  const finishAndNavigate = useCallback(async () => {
    if (isDemoUser(user)) {
      await redirectDemoToPlans();
      return;
    }
    navigate("/welcome");
  }, [user, navigate]);

  const handleImageComplete = useCallback(async () => {
    if (!imageFile) {
      Swal.fire("No image", "Please take a selfie or upload a file first.", "warning");
      return;
    }
    if (!user || !adminToken) {
      Swal.fire("Session expired", "Please log in again before uploading.", "error");
      return;
    }

    setSubmitting(true);
    try {
      await uploadYogaMediaViaPresign({
        files: [imageFile],
        userId: user.userId,
        time: "00:00",
        token: adminToken,
      });
      await addReportToServer(user, {
        points: 0,
        reportTime: "00:00:00",
        standalone: true,
      });
      stopCamera();
      await finishAndNavigate();
    } catch (e) {
      Swal.fire("Upload failed", String(e?.message || e), "error");
    } finally {
      setSubmitting(false);
    }
  }, [imageFile, user, adminToken, stopCamera, finishAndNavigate]);

  const handleWordComplete = useCallback(async () => {
    const word = String(playerWord ?? "").trim().slice(0, WORDS_MAX_LEN);
    if (!word) {
      Swal.fire("Enter a word", `Please enter a word (max ${WORDS_MAX_LEN} characters).`, "warning");
      return;
    }
    if (!user || !adminToken) {
      Swal.fire("Session expired", "Please log in again before submitting.", "error");
      return;
    }
    const themeId = currentTheme || themeData?.themename;
    if (!themeId) {
      Swal.fire("Theme missing", "Could not find the active theme. Please refresh and try again.", "error");
      return;
    }

    setSubmitting(true);
    try {
      // Refresh first so concurrent player submissions are not overwritten.
      const fresh = await dispatch(fetchThemeData({ themeId })).unwrap();
      const existing = parsePlayerWordsList(fresh?.data?.player_words ?? themeData?.player_words);
      const nextWords = [
        ...existing,
        { word, userId: user.userId, at: Date.now() },
      ].slice(-200);

      await updateThemeData({
        payload: {
          data: { player_words: nextWords },
          currentTheme: themeId,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId })).unwrap();
      await addReportToServer(user, {
        points: 0,
        reportTime: "00:00:00",
        standalone: true,
        answers: ["word-submit"],
      });
      markWordDone(user);
      await finishAndNavigate();
    } catch (e) {
      Swal.fire("Submit failed", String(e?.message || e), "error");
    } finally {
      setSubmitting(false);
    }
  }, [playerWord, user, adminToken, currentTheme, themeData?.themename, themeData?.player_words, dispatch, finishAndNavigate]);

  // Skip if already contributed for this session.
  useEffect(() => {
    if (!user || !adminToken) {
      setChecking(false);
      return undefined;
    }

    // Word mode: local completion flag (no image upload row).
    if (wordMode) {
      if (hasWordDone(user)) {
        if (isDemoUser(user)) {
          redirectDemoToPlans();
          return undefined;
        }
        navigate("/game", { replace: true });
        return undefined;
      }
      setChecking(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!cancelled && data?.success && data.hasUpload) {
          if (isDemoUser(user)) {
            await redirectDemoToPlans();
            return;
          }
          navigate("/game", { replace: true });
          return;
        }
      } catch {
        /* fall through to showing the upload UI */
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken, navigate, wordMode]);

  // Attach the stream once the <video> is actually mounted (after cameraOn flips).
  useEffect(() => {
    if (cameraOn && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOn]);

  useEffect(() => stopCamera, [stopCamera]);

  // Leave camera off when switching into word mode.
  useEffect(() => {
    if (wordMode) stopCamera();
  }, [wordMode, stopCamera]);

  if (checking) {
    return (
      <div className="ui-page" style={{ color: textColor }}>
        <div className="quiz-loader-container">
          <div className="quiz-loader" />
        </div>
      </div>
    );
  }

  if (wordMode) {
    return (
      <div className="ui-page" style={{ color: textColor }}>
        <h1 className="ui-title">ENTER YOUR WORD</h1>

        <input
          className="ui-word-input"
          type="text"
          value={playerWord}
          maxLength={WORDS_MAX_LEN}
          placeholder="Your word"
          onChange={(e) => setPlayerWord(String(e.target.value ?? "").slice(0, WORDS_MAX_LEN))}
          aria-label="Mosaic word"
        />
        <div className="ui-word-hint">
          {playerWord.length}/{WORDS_MAX_LEN} characters
        </div>

        <div className="ui-instructions">
          <p className="ui-instructions-title">Instructions:</p>
          <ol>
            <li>Enter a word (max {WORDS_MAX_LEN} characters)</li>
            <li>Press submit</li>
            <li>Your word will appear in the mosaic</li>
          </ol>
        </div>

        <button
          type="button"
          className="ui-btn ui-btn--complete"
          style={btnStyle}
          onClick={handleWordComplete}
          disabled={submitting}
        >
          {submitting ? "SUBMITTING…" : "COMPLETE"}
        </button>
      </div>
    );
  }

  return (
    <div className="ui-page" style={{ color: textColor }}>
      <h1 className="ui-title">UPLOAD YOUR IMAGE</h1>

      <button type="button" className="ui-btn" style={btnStyle} onClick={startCamera}>
        TAKE A SELFIE
      </button>

      <div className="ui-or">OR</div>

      <button
        type="button"
        className="ui-btn"
        style={btnStyle}
        onClick={() => fileInputRef.current?.click()}
      >
        UPLOAD FILE
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="ui-hidden-input"
        onChange={handleFileChange}
      />

      <div className="ui-instructions">
        <p className="ui-instructions-title">Instructions:</p>
        <ol>
          <li>Select a photo (jpg, jpeg or png)</li>
          <li>Press submit to upload</li>
          <li>Files must be less than 2MB</li>
        </ol>
      </div>

      {selfieMode ? (
        <div className="ui-capture">
          <h2 className="ui-capture-title">Capture Your Picture</h2>
          <div className="ui-stage">
            {cameraOn ? (
              <video ref={videoRef} className="ui-media" playsInline muted />
            ) : previewUrl ? (
              <img src={previewUrl} className="ui-media" alt="Your selfie" />
            ) : null}
          </div>
          <button
            type="button"
            className="ui-btn ui-btn--snap"
            style={btnStyle}
            onClick={cameraOn ? takeSnapshot : startCamera}
          >
            {cameraOn ? "Take Snapshot" : "Retake Snapshot"}
          </button>
        </div>
      ) : previewUrl ? (
        <div className="ui-capture">
          <div className="ui-stage">
            <img src={previewUrl} className="ui-media" alt="Your upload" />
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="ui-btn ui-btn--complete"
        style={btnStyle}
        onClick={handleImageComplete}
        disabled={submitting}
      >
        {submitting ? "UPLOADING…" : "COMPLETE"}
      </button>
    </div>
  );
}
